'use client';
import { useState, useEffect } from "react";

// Styles
import * as styles from "./MainScreen.module.css";

// Components
import Question from "../Question/Question";
import QuestionWithAnswer from "../QuestionWithAnswer/QuestionWithAnswer";
import NavigationBar from '../NavigationBar/NavigationBar';
import ChatField from "../ChatField/ChatField";
import FeedbackPopUp from "../FeedbackPopUp/FeedbackPopUp";

// Server API-endpoints
import { getProductQuestions } from "../../api/onboarding";
import { fetchNewQuestion } from "../../api/proceed";
import { trackChatQuestion } from "../../api/trackChatQuestion";
import { trackQuestionFeedback } from "../../api/trackQuestionFeedback";
import { trackGeneralFeedback } from "../../api/trackGeneralFeedback";
import { trackTextFeedback } from "../../api/trackTextFeedback";

export default function MainScreen({ productLink, sessionId, customerId, website, productNumber, userId, handlePlacement }) {

  // Objects
  const [questions, setQuestions] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [error, setError] = useState(null);
  const [focusedQuestionIndex, setFocusedQuestionIndex] = useState(null);
  const [pendingNewQuestion, setPendingNewQuestion] = useState(null);
  const [newQuestionId, setNewQuestionId] = useState(null);
  const [streamedQuestions, setStreamedQuestions] = useState(new Set());

  // State
  const [loading, setLoading] = useState(false);
  const [disableActions, setDisableActions] = useState(false);
  const [widgetEnabled, setWidgetEnabled] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showFeedbackPopUp, setShowFeedbackPopUp] = useState(false);

  // Config
  const [popUpMessage, setPopUpMessage] = useState("Dette svaret er generert av en AI-modell og kan inneholde feil eller unøyaktigheter.");
  const [showBetaVersion, setShowBetaVersion] = useState(false);
  const [trackAddToCart, setTrackAddToCart] = useState(false);

  // Add these new state variables inside MainScreen component
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);

  useEffect(() => {
    async function fetchQuestions() {
      if (!productLink && !productNumber) {
        setQuestions([]);
        setDataLoaded(true);
        setWidgetEnabled(false);
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        // Read test IDs from sessionStorage
        const testId = sessionStorage.getItem('truste-qa-test-id');
        const testGroupId = sessionStorage.getItem('truste-qa-test-group-id');

        const data = await getProductQuestions(
          productLink, 
          sessionId, 
          customerId, 
          website, 
          productNumber, 
          userId,
          testId, // Pass testId
          testGroupId // Pass testGroupId
        );
        
        if (!data) {
          setQuestions([]);
          return;
        }

        // Store returned test IDs in sessionStorage if they exist
        if (data.test_id) {
          sessionStorage.setItem('truste-qa-test-id', data.test_id);
        }
        if (data.test_group_id) {
          sessionStorage.setItem('truste-qa-test-group-id', data.test_group_id);
        }

        setWidgetEnabled(data.flags?.show_widget ?? false);
        if (data.flags?.track_add_to_cart) {
          setTrackAddToCart(data.flags.track_add_to_cart);
          // Update the global config
          if (typeof window !== 'undefined') {
            window.qaWidgetConfig = {
              ...window.qaWidgetConfig,
              trackAddToCart: data.flags.track_add_to_cart
            };
          }
        }
        
        if (data.flags?.show_widget && Array.isArray(data.qa)) {
          setQuestions(data.qa);
          setFocusedQuestionIndex(null);
          setUpConfig(data);
        } else {
          setQuestions([]);
        }
      } catch (err) {
        setError(err.message);
        setQuestions([]);
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    }

    fetchQuestions();
  }, [productLink, productNumber, sessionId, customerId, website, userId]);

  function setUpConfig(data) {
    if (data?.config?.pop_up_message) {
      setPopUpMessage(data.config.pop_up_message);
    } 

    if (data?.placement) {
        handlePlacement(data.placement);
    }

    // Apply theme configuration - updated to use theme_config
    if (data?.config?.theme_config) {
      const theme = data.config.theme_config;
      const root = document.documentElement;

      // Base theme properties
      theme.background_color && root.style.setProperty('--qa-backgroundColor', theme.background_color);
      theme.font_family && root.style.setProperty('--qa-fontFamily', theme.font_family);

      // Question properties
      if (theme.question) {
        theme.question.font_size && root.style.setProperty('--qa-questionFontSize', theme.question.font_size);
        theme.question.font_color && root.style.setProperty('--qa-questionTextColor', theme.question.font_color);
        theme.question.icon_color && root.style.setProperty('--qa-questionIconColor', theme.question.icon_color);
        theme.question.background_color && root.style.setProperty('--qa-questionBackgroundColor', theme.question.background_color);
        theme.question.border_color && root.style.setProperty('--qa-questionBorderColor', theme.question.border_color);
      }

      // Focus section properties
      if (theme.focus_section) {
        theme.focus_section.border_color && root.style.setProperty('--qa-focusSectionBorderColor', theme.focus_section.border_color);
        theme.focus_section.separator_color && root.style.setProperty('--qa-focusSectionSeperatorBorderColor', theme.focus_section.separator_color);

        // Focus section question
        if (theme.focus_section.question) {
          theme.focus_section.question.font_size && root.style.setProperty('--qa-focusSectionQuestionFontSize', theme.focus_section.question.font_size);
          theme.focus_section.question.font_color && root.style.setProperty('--qa-focusSectionQuestionTextColor', theme.focus_section.question.font_color);
          theme.focus_section.question.background_color && root.style.setProperty('--qa-focusSectionQuestionBackgroundColor', theme.focus_section.question.background_color);
          theme.focus_section.question.font_weight && root.style.setProperty('--qa-focusSectionQuestionFontWeight', theme.focus_section.question.font_weight);
        }

        // Focus section answer
        if (theme.focus_section.answer) {
          theme.focus_section.answer.font_size && root.style.setProperty('--qa-focusSectionAnswerFontSize', theme.focus_section.answer.font_size);
          theme.focus_section.answer.font_color && root.style.setProperty('--qa-focusSectionAnswerTextColor', theme.focus_section.answer.font_color);
          theme.focus_section.answer.background_color && root.style.setProperty('--qa-focusSectionAnswerBackgroundColor', theme.focus_section.answer.background_color);
        }

        // Feedback thumbs
        if (theme.focus_section.feedback_thumbs) {
          theme.focus_section.feedback_thumbs.color && root.style.setProperty('--qa-thumbsColor', theme.focus_section.feedback_thumbs.color);
        }

        // Disclaimer popup
        if (theme.focus_section.disclaimer_pop_up) {
          theme.focus_section.disclaimer_pop_up.background_color && root.style.setProperty('--qa-disclaimerPopupBackgroundColor', theme.focus_section.disclaimer_pop_up.background_color);
          theme.focus_section.disclaimer_pop_up.font_color && root.style.setProperty('--qa-disclaimerPopupTextColor', theme.focus_section.disclaimer_pop_up.font_color);
        }
      }

      // Navbar properties
      if (theme.navbar) {
        theme.navbar.active_color && root.style.setProperty('--qa-navbarActiveTextColor', theme.navbar.active_color);
        theme.navbar.inactive_color && root.style.setProperty('--qa-navbarTextColor', theme.navbar.inactive_color);
      }

      // Chatfield properties
      if (theme.chatfield) {
        theme.chatfield.font_size && root.style.setProperty('--qa-chatFieldFontSize', theme.chatfield.font_size);
        theme.chatfield.font_color && root.style.setProperty('--qa-chatfieldTextColor', theme.chatfield.font_color);
        theme.chatfield.icon_color && root.style.setProperty('--qa-chatfieldIconColor', theme.chatfield.icon_color);
        theme.chatfield.background_color && root.style.setProperty('--qa-chatfieldBackgroundColor', theme.chatfield.background_color);
        theme.chatfield.border_color && root.style.setProperty('--qa-chatfieldBorderColor', theme.chatfield.border_color);
        theme.chatfield.placeholder_color && root.style.setProperty('--qa-chatfieldPlaceholderColor', theme.chatfield.placeholder_color);
      }
    }
  }

  async function getNewQuestion(clickedQuestionId) {
    try {
      const allQuestions = answeredQuestions.length === 0 
        ? questions 
        : [...questions, ...answeredQuestions];
      
      const response = await fetchNewQuestion(allQuestions, productLink, clickedQuestionId, answeredQuestions, sessionId, customerId, website, productNumber, userId);
      const newQuestion = response.qa;
      
      // Only update state if we received a valid question and it's not already in the list
      if (newQuestion) {
        setQuestions(current => {
          // Check if the question already exists in the current list
          if (current.some(q => q.id === newQuestion.id)) {
            return current;
          }
          return [newQuestion, ...current];
        });
        setNewQuestionId(newQuestion.id);
      } 
    } catch (error) {
      console.log('Error fetching new question:', error);
    }
  }

  function handleQuestionClick(index) {
    if (disableActions) return;

    setDisableActions(true);
    const clickedQuestion = questions[index];

    // Check if question is already in answeredQuestions to prevent duplicates
    setAnsweredQuestions(prev => {
      if (prev.some(q => q.id === clickedQuestion.id)) {
        setDisableActions(false);
        return prev;
      }
      const newAnsweredQuestions = [...prev, clickedQuestion];
      // Show feedback popup after 5th question
      if (newAnsweredQuestions.length === 4) {
        setShowFeedbackPopUp(true);
      }
      return newAnsweredQuestions;
    });

    // Only remove the question if it was successfully added to answeredQuestions
    setQuestions(current => current.filter(q => q.id !== clickedQuestion.id));
    setFocusedQuestionIndex(answeredQuestions.length);

    setNewQuestionId(null);
    getNewQuestion(clickedQuestion.id);
  }

  const onStreamComplete = () => {
    setDisableActions(false);
    if (pendingNewQuestion) {
      setQuestions(current => [pendingNewQuestion, ...current]);
      setNewQuestionId(pendingNewQuestion.id);
      setPendingNewQuestion(null);

      setTimeout(() => {
      }, 200);
    }
  };

  const handleAddAnswer = (id, answer) => {
    setAnsweredQuestions(prev => {
        const newQuestions = [...prev];
        const questionIndex = newQuestions.findIndex(q => q.id === id);
        if (questionIndex !== -1) {
            newQuestions[questionIndex].answer = answer;
        }

        return newQuestions;
    });

    const clickedQuestion = answeredQuestions.find(q => q.id === id); // Find the clicked question
    clickedQuestion.answer = answer; // Add the answer to the clicked question

    trackChatQuestion(productLink, clickedQuestion, answeredQuestions, sessionId, customerId, website, productNumber); // Track the clicked question
  }

  const handleSend = async (message) => {

    setDisableActions(true);
    
    const tempQuestionObj = {
      question: message,
      answer: null,
      keyword: message,
      id: crypto.randomUUID(),
      productLink: productLink
    }
    setAnsweredQuestions(prev => [...prev, tempQuestionObj]);
    setFocusedQuestionIndex(answeredQuestions.length);
  };

  const handleQuestionFeedbackClick = (questionId, type) => {
    setAnsweredQuestions(prev => {
      const newQuestions = [...prev];
      const questionIndex = newQuestions.findIndex(q => q.id === questionId);
      if (questionIndex !== -1) {
        newQuestions[questionIndex].feedback = type;
        // Track the feedback after updating state
        const question = newQuestions[questionIndex];
        trackQuestionFeedback(
          productLink,
          question.id,
          type,
          sessionId,
          customerId,
          website,
          productNumber
        );
      }
      return newQuestions;
    });
  };

  // Add these new functions inside MainScreen component before the return statement
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
    
    // Add swipe animation while moving with dynamic rotation
    if (touchStart && e.touches[0].clientX) {
      const distance = touchStart - e.touches[0].clientX;
      const maxRotation = 10; // maximum rotation in degrees
      const rotationFactor = Math.min(Math.abs(distance) / 100, 1); // normalize the rotation
      
      if (Math.abs(distance) > 5) { // Smaller threshold for visual feedback
        const style = {
          transform: `translateX(${-distance/10}px) rotateY(${distance > 0 ? -rotationFactor * maxRotation : rotationFactor * maxRotation}deg)`,
          opacity: 1 - (rotationFactor * 0.2) // subtle opacity change
        };
        e.currentTarget.style.transform = style.transform;
        e.currentTarget.style.opacity = style.opacity;
        setSwipeDirection(distance > 0 ? 'left' : 'right');
      }
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) > 50;

    if (isSwipe) {
      if (distance > 0) {
        if (focusedQuestionIndex < answeredQuestions.length - 1) {
          setFocusedQuestionIndex(focusedQuestionIndex + 1);
        }
      } else {
        if (focusedQuestionIndex > 0) {
          setFocusedQuestionIndex(focusedQuestionIndex - 1);
        }
      }
    }

    // Reset the transform and opacity
    const element = document.querySelector(`.${styles.focusSection}`);
    if (element) {
      element.style.transform = '';
      element.style.opacity = '';
    }

    setTouchStart(null);
    setTouchEnd(null);
    setSwipeDirection(null);
  };

  if (!dataLoaded || !productLink || !widgetEnabled) {
    return null;
  }

  if (loading) {
    return (
      <div className={styles.mainScreenContainer}>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.mainScreenContainer}>
      </div>
    );
  }

  const onFeedbackPopUpClose = () => {
    setShowFeedbackPopUp(false);
  }

  const onGeneralFeedbackSubmit = (feedback) => {
    trackGeneralFeedback(productLink, feedback, sessionId, customerId, website);
  }

  const onTextFeedbackSubmit = (feedback) => {
    trackTextFeedback(productLink, feedback, sessionId, customerId, website);
  }

  return (
    <div className={styles.mainScreenContainer}>
      <div className={styles.contentContainer}>
        {showBetaVersion && <div className={styles.header}>Beta-versjon</div>}
        {showFeedbackPopUp && <FeedbackPopUp onClose={onFeedbackPopUpClose} onGeneralFeedbackSubmit={onGeneralFeedbackSubmit} onTextFeedbackSubmit={onTextFeedbackSubmit} />}
        {questions.length === 0 && answeredQuestions.length === 0 ? (
          null
        ) : (
          <>
            {focusedQuestionIndex !== null && (
              <div className={styles.focusContainer}>
                <div 
                  className={`${styles.focusSection} ${swipeDirection === 'left' ? styles.swipingLeft : swipeDirection === 'right' ? styles.swipingRight : ''}`}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <QuestionWithAnswer
                    question={answeredQuestions[focusedQuestionIndex].question}
                    answer={answeredQuestions[focusedQuestionIndex].answer}
                    shouldStream={!streamedQuestions.has(focusedQuestionIndex)}
                    onStreamComplete={() => {
                      setStreamedQuestions(prev => new Set(prev).add(focusedQuestionIndex));
                      onStreamComplete();
                    }}
                    productLink={productLink}
                    questionId={answeredQuestions[focusedQuestionIndex].id}
                    onAddAnswer={handleAddAnswer}
                    feedback={answeredQuestions[focusedQuestionIndex].feedback}
                    onFeedback={handleQuestionFeedbackClick}
                    customerId={customerId}
                    sessionId={sessionId}
                    website={website}
                    productNumber={productNumber}
                    questionHistory={answeredQuestions}
                    popUpMessage={popUpMessage}
                    setFocusedQuestionIndex={setFocusedQuestionIndex}
                    answeredQuestions={answeredQuestions}
                  />
                </div>
                <NavigationBar
                  answeredQuestions={answeredQuestions}
                  focusedQuestionIndex={focusedQuestionIndex}
                  setFocusedQuestionIndex={setFocusedQuestionIndex}
                />
              </div>
            )}

            {questions.length > 0 && (
            <div className={styles.questionsList}>
              {questions.filter((question, index, self) => 
                // Only keep the first occurrence of each question ID
                index === self.findIndex(q => q.id === question.id)
              ).map((question, index) => (
                <div 
                  key={`${question.id}-${index}`}
                  className={`${styles.questionItem} ${question.id === newQuestionId ? styles.slideIn : ''}`}
                >
                  <Question
                    question={question.question}
                    onClick={() => handleQuestionClick(index)}
                  />
                </div>
              ))}
            </div>
            )}
          </>
        )}
        <ChatField onSend={handleSend} />
        <div className={styles.footer}>
          <div>Powered by <a href="https://truste.ai" target="_blank" rel="noopener noreferrer" className={styles.trusteLogo}>Truste</a></div>
        </div>
      </div>
    </div>
  );
}
